import type { ReactNode } from "react";
import {
  Card,
  Badge,
  Button,
  BlockStack,
  InlineStack,
  Text,
  Box,
  Divider,
} from "@shopify/polaris";
import { CircleChevronRightIcon } from "@shopify/polaris-icons";
import type { Game } from "../../api/campaigns";

export interface GameCardProps {
  game: Game;
  statusBadge: ReactNode;
  preview?: ReactNode;
  onUseInCampaign: () => void;
}

/**
 * Catalog row for one game: metadata + optional built-in preview + primary CTA.
 * Previews are game-specific and render only when provided (scales to many games).
 */
export function GameCard({ game, statusBadge, preview, onUseInCampaign }: GameCardProps) {
  const version = game.versions?.[0];

  return (
    <Card>
      <Box padding="500">
        <BlockStack gap="400">
          <InlineStack align="space-between" blockAlign="start" gap="400" wrap>
            <BlockStack gap="200">
              <InlineStack gap="300" blockAlign="center" wrap>
                <Text as="h2" variant="headingMd" fontWeight="semibold">
                  {game.name}
                </Text>
                {statusBadge}
              </InlineStack>
              {game.description && (
                <Text as="p" variant="bodyMd" tone="subdued">
                  {game.description}
                </Text>
              )}
            </BlockStack>

            {preview ? (
              <Box
                paddingInline="200"
                paddingBlock="200"
                background="bg-surface-secondary"
                borderRadius="200"
                minWidth="180px"
                maxWidth="220px"
              >
                {preview}
              </Box>
            ) : null}
          </InlineStack>

          {game.tags && game.tags.length > 0 && (
            <InlineStack gap="200" wrap>
              {game.tags.map((tag) => (
                <Badge key={tag}>{tag}</Badge>
              ))}
            </InlineStack>
          )}

          {version && (
            <>
              <Divider />
              <InlineStack align="space-between" blockAlign="center" wrap gap="300">
                <BlockStack gap="100">
                  <Text as="p" variant="bodySm" tone="subdued">
                    Version {version.version}
                  </Text>
                  {version.placements && version.placements.length > 0 && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Placements: {version.placements.join(", ")}
                    </Text>
                  )}
                </BlockStack>
                <Button variant="primary" icon={CircleChevronRightIcon} onClick={onUseInCampaign}>
                  Use in Campaign
                </Button>
              </InlineStack>
            </>
          )}
        </BlockStack>
      </Box>
    </Card>
  );
}
